import type { NavigateOptions } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import type { User } from '@/stores/auth'
import { opportunityTabs } from '../-components/tabs'

export interface UseOpportunityUrlSyncOptions {
  /** 検索パラメータからのタブ値 */
  searchParamsTab?: string
  /** 検索パラメータからの検索文字列 */
  searchParamsSearch?: string
  /** デフォルトタブキー */
  defaultTab: string
  /** navigate関数 */
  navigate: (options: NavigateOptions) => Promise<void>
  /** ユーザー情報 */
  user: User | null
}

/**
 * タブと検索テキストをURLと同期するカスタムフック
 */
export function useOpportunityUrlSync(options: UseOpportunityUrlSyncOptions) {
  const { searchParamsTab, searchParamsSearch, defaultTab, navigate, user } = options

  // タブと検索の状態
  const [selectedTab, setSelectedTab] = useState<string>(searchParamsTab || defaultTab)
  const [searchText, setSearchText] = useState(searchParamsSearch || '')

  // URL変更時にタブを更新（初期表示時にtabパラメータがない場合も含む）
  useEffect(() => {
    if (!user) return

    if (searchParamsTab) {
      // URLにtabパラメータがある場合: 権限チェックして適用
      const requestedTab = opportunityTabs.find((tab) => tab.key === searchParamsTab)
      if (
        !requestedTab ||
        requestedTab.isDisabled({
          isCaptain: user.isCaptain,
          isServiceManager: user.isServiceManager,
          isProjectManager: user.isProjectManager,
          isSalesMember: user.isSalesMember,
          acl: user.acl,
        })
      ) {
        setSelectedTab('owner')
        navigate({
          search: (prev: { tab?: string; search?: string }) => ({ ...prev, tab: 'owner' }),
        })
      } else {
        setSelectedTab(searchParamsTab)
      }
    } else {
      // URLにtabパラメータがない場合: デフォルトタブでURLを更新（履歴を置き換え）
      setSelectedTab(defaultTab)
      navigate({
        search: (prev: { tab?: string; search?: string }) => ({ ...prev, tab: defaultTab }),
        replace: true,
      })
    }

    // URLの検索パラメータと状態を同期（undefinedの場合は空文字にリセット）
    setSearchText(searchParamsSearch || '')
  }, [searchParamsSearch, searchParamsTab, user, navigate, defaultTab])

  // タブ変更時にURLを更新
  const handleTabChange = useCallback(
    (newTab: string) => {
      setSelectedTab(newTab)
      navigate({
        search: (prev: { tab?: string; search?: string }) => ({ ...prev, tab: newTab }),
      })
    },
    [navigate],
  )

  // 検索テキスト変更時にURLを更新（デバウンス付き）
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate({
        search: (prev: { tab?: string; search?: string }) => ({ ...prev, search: searchText || undefined }),
      })
    }, 1000)
    return () => clearTimeout(timer)
  }, [searchText, navigate])

  return {
    selectedTab,
    setSelectedTab,
    searchText,
    setSearchText,
    handleTabChange,
  }
}
